import React, { useState, useEffect, useRef } from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Legend, ComposedChart
} from 'recharts';
import { AURAVEX_LOGO } from './logo';
import { INR, PLANS, SITES_DATA, Badge, Dot, KPI, Panel, Prog, Pill, BtnStyles, Btn, Inp, Sel, Textarea, Modal, Toast, DataTable, PageHeader, AlertBox, Grid, Divider, CameraLiveModal, alertHours, trafficHours, sc} from './utils';

// ============================================================
// LIVE API INTEGRATION - AURAVEX Backend
// ============================================================
const API_NODE   = (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL)
  ? process.env.REACT_APP_API_URL : '';
const API_PYTHON = (typeof process !== 'undefined' && process.env?.REACT_APP_PYTHON_URL)
  ? process.env.REACT_APP_PYTHON_URL : 'http://localhost:8000';
const WS_URL_BASE = (typeof process !== 'undefined' && process.env?.REACT_APP_WS_URL)
  ? process.env.REACT_APP_WS_URL : 'ws://localhost:5000/ws';

const apiFetch = async (path, opts = {}, base = API_NODE) => {
  try {
    const token = localStorage.getItem('auravex_token');
    const res = await fetch(base + path, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...opts.headers,
      },
      ...opts,
    });
    if (res.status === 401) {
      localStorage.removeItem('auravex_token');
      localStorage.removeItem('auravex_user');
      window.dispatchEvent(new CustomEvent('auravex:401'));
      return null;
    }
    if (!res.ok) {
      let errMsg = `HTTP ${res.status}`;
      try { const j = await res.json(); errMsg = j.error || j.message || errMsg; } catch {}
      throw new Error(errMsg);
    }
    return await res.json();
  } catch (e) {
    console.warn(`[API] ${opts.method||'GET'} ${path}:`, e.message);
    throw e;  // re-throw so callers can handle it
  }
};
const pyFetch   = (p, o={}) => apiFetch(p, o, API_PYTHON);
const apiPost   = (p, d) => apiFetch(p, { method: 'POST',   body: JSON.stringify(d) });
const apiPut    = (p, d) => apiFetch(p, { method: 'PUT',    body: JSON.stringify(d) });
const apiPatch  = (p, d) => apiFetch(p, { method: 'PATCH',  body: JSON.stringify(d) });
const apiDelete = (p)    => apiFetch(p, { method: 'DELETE' });

// Map DB snake_case -> frontend camelCase
const mapTenant = r => r ? ({
  ...r,
  plan:    r.plan_id   ?? r.plan   ?? 'starter',
  maxCam:  r.max_cameras ?? r.max_cam ?? r.maxCam ?? 0,
  maxU:    r.max_users   ?? r.max_u   ?? r.maxU   ?? 0,
}) : null;
const mapCamera = r => r ? ({
  ...r,
  siteId:  r.site_id    ?? r.siteId  ?? '',
  res:     r.resolution ?? r.res     ?? '1080p',
  models:  Array.isArray(r.models) ? r.models : (typeof r.models==='string' ? JSON.parse(r.models||'[]') : []),
}) : null;
const mapEvent  = r => r ? ({
  ...r,
  caseId:  r.case_id   ?? r.caseId  ?? '',
  tId:     r.tenant_id ?? r.t_id    ?? r.tId    ?? '',
  siteId:  r.site_id   ?? r.siteId  ?? '',
  camId:   r.cam_id    ?? r.camId   ?? '',
  sev:     r.severity  ?? r.sev     ?? 'Medium',
  evidence: Array.isArray(r.evidence) ? r.evidence : [],
}) : null;
const mapUser   = r => r ? ({
  id:        r.id,
  name:      r.name,
  email:     r.email,
  role:      r.role,
  tenantId:  r.tenant_id   ?? r.tenantId ?? '',
  status:    r.status      ?? 'Active',
  mfa:       r.mfa_enabled ?? r.mfa      ?? false,
  last:      r.last_login  ?? r.last_seen ?? r.last ?? '',
  mobiles:   Array.isArray(r.mobiles)  ? r.mobiles  : (typeof r.mobiles ==='string' ? JSON.parse(r.mobiles  ||'[]') : []),
  emails:    Array.isArray(r.emails)   ? r.emails   : (typeof r.emails  ==='string' ? JSON.parse(r.emails   ||'[]') : [r.email||'']),
  notifyVia: (typeof r.notify_via==='object' && r.notify_via!==null) ? r.notify_via : (typeof r.notify_via==='string' ? JSON.parse(r.notify_via||'{}') : r.notifyVia || {email:true}),
}) : null;
const mapJetson = r => r ? ({ ...r, siteId: r.site_id??r.siteId, tId: r.tenant_id??r.t_id??r.tId }) : null;
const mapViol   = r => r ? ({ ...r, camId: r.cam_id??r.camId }) : null;
/* -- API payload converters (Frontend → Backend field names) -- */
const tenantToAPI  = t => ({ name:t.name, sector:t.sector, plan_id:t.plan||t.plan_id||'starter', contact:t.contact||'', billing:t.billing||'monthly', renewal:t.renewal||null, status:t.status, onboarded:t.onboarded||false });
const cameraToAPI  = c => ({ site_id:c.siteId||c.site_id||null, name:c.name, brand:c.brand||'Hikvision', type:c.type||'Fixed', ip:c.ip||'', resolution:c.res||c.resolution||'1080p', fps:parseInt(c.fps)||25, models:c.models||[], retention:c.retention||'30d', zone:c.zone||'', status:c.status||'Online', health:c.health!=null?c.health:100, recording:c.recording!=null?c.recording:true, rtsp_url:c.rtsp_url||c.rtspUrl||'', cam_user:c.cam_user||c.camUser||null, cam_pass:c.cam_pass||c.camPass||null });
const userToAPI    = u => ({ name:u.name, email:u.email||(u.emails&&u.emails[0])||'', role:u.role, tenant_id:u.tenantId||u.tenant_id||null, mobiles:u.mobiles||[], emails_list:u.emails||[], notify_via:u.notifyVia||u.notify_via||{email:true}, mfa_enabled:u.mfa||false, status:u.status });
const ruleToAPI    = r => ({ name:r.name, trigger:r.trigger||'', condition:r.condition||'', action:r.action||'', tenant_id:r.tId||r.tenant_id||'All', status:r.status||'Active' });

/* -- SECTOR AVATARS ------------------------------------------- */
const AVATAR_GOV  = "data:image/jpeg;base64,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";   // Male  - Government
const AVATAR_PVT  = "data:image/jpeg;base64,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"; // Female - Private / Industrial
const sectorAvatar = (sector) =>
  (sector==="Government"||sector==="Municipal"||sector==="Defence"||sector==="Police")
    ? AVATAR_GOV : AVATAR_PVT;

/* -- DESIGN TOKENS -- CLEAN WHITE LIGHT THEME ------------------- */
/* -- CAMERA STREAM URL HELPER -- generates HTTP preview URL per brand -- */
const BRAND_SNAPSHOT = {
  'Hikvision':  (ip) => `http://${ip}/ISAPI/Streaming/channels/101/picture`,
  'Dahua':      (ip) => `http://${ip}/cgi-bin/snapshot.cgi?channel=1`,
  'CP Plus':    (ip) => `http://${ip}/cgi-bin/snapshot.cgi?channel=1`,
  'Axis':       (ip) => `http://${ip}/axis-cgi/jpg/image.cgi?resolution=1280x720`,
  'Hanwha':     (ip) => `http://${ip}/stw-cgi/video.cgi?msubmenu=snapshot&action=view`,
  'Bosch':      (ip) => `http://${ip}/snap.jpg?JpegSize=L`,
  'Uniview':    (ip) => `http://${ip}/cgi-bin/snapshot.cgi`,
};
const BRAND_MJPEG = {
  'Hikvision':  (ip) => `http://${ip}/ISAPI/Streaming/channels/102/httpPreview`,
  'Dahua':      (ip) => `http://${ip}/cgi-bin/mjpg/video.cgi?channel=1&subtype=1`,
  'CP Plus':    (ip) => `http://${ip}/cgi-bin/mjpg/video.cgi?channel=1&subtype=1`,
  'Axis':       (ip) => `http://${ip}/axis-cgi/mjpg/video.cgi?resolution=640x480`,
  'Hanwha':     (ip) => `http://${ip}/stw-cgi/video.cgi?msubmenu=mjpeg&action=view`,
  'Bosch':      (ip) => `http://${ip}/video.mjpg`,
  'Uniview':    (ip) => `http://${ip}/cgi-bin/mjpg/video.cgi?channel=1`,
};
const extractCamIp = (ipOrUrl) => { if(!ipOrUrl) return ''; const m=ipOrUrl.match(/(\d+\.\d+\.\d+\.\d+)/); return m?m[1]:ipOrUrl; };
let _jetsonIp = null;
let _jetsonTs = 0;
const getStreamUrl = (cam) => {
  if(!cam||!cam.ip) return { url:'', type:'none' };
  // Retry every 10s if previous attempt failed, or fetch once if never tried
  const now = Date.now();
  if (_jetsonIp === null || (_jetsonIp === '' && now - _jetsonTs > 10000)) {
    _jetsonTs = now;
    try {
      const t = localStorage.getItem('auravex_token');
      if (t) {
        const x = new XMLHttpRequest();
        x.open('GET','/api/jetsons',false);
        x.setRequestHeader('Authorization','Bearer '+t);
        x.send();
        if (x.status === 200) {
          const j = JSON.parse(x.responseText);
          _jetsonIp = (j && j.length > 0 && j[0].ip) ? j[0].ip : '';
        } else { _jetsonIp = ''; }
      } else { _jetsonIp = ''; }
    } catch(e) { _jetsonIp = ''; }
  }
  if (_jetsonIp) {
    return { url: 'http://'+_jetsonIp+':8080/stream?cam='+encodeURIComponent(cam.id||''), type:'mjpeg' };
  }
  return { url:'', type:'none' };
};
const T = {
  /* -- Surface hierarchy -- */
  bg:"#F0F2F7",     /* Page background -- very light grey-blue */
  deep:"#FFFFFF",   /* Sidebar background */
  panel:"#FFFFFF",  /* Card panels */
  card:"#FFFFFF",   /* Inline cards */
  border:"#E2E8F3", /* Subtle borders */
  hi:"#EEF2FB",     /* Hover highlight */
  glow:"#DDE5F6",   /* Active glow */
  faint:"#F7F9FD",  /* Input backgrounds */
  /* Gold primary -- AURAVEX brand */
  accent:"#C48A0E", accentD:"#9A6B00", accentLo:"#C48A0E18",
  /* Electric blue */
  blue:"#1A5FB4",   blueLo:"#1A5FB414",
  /* Green */
  green:"#1E8A55",  greenLo:"#1E8A5514",
  /* Standard semantic */
  red:"#C0392B",    redLo:"#C0392B12",
  purple:"#6527BE", purpleLo:"#6527BE12",
  teal:"#0E7490",   tealLo:"#0E749012",
  orange:"#C05800", orangeLo:"#C0580012",
  pink:"#A21060",   pinkLo:"#A2106012",
  /* Text -- dark on white */
  text:"#1A2235",   muted:"#7A90B0",
  /* Silver */
  silver:"#4A607A",
  font:"'DM Sans',system-ui,sans-serif",
  mono:"'IBM Plex Mono','Fira Mono',monospace",
};

export const PageDashboard = ({events,tenants,cameras,violations}) => {
  const [discovery, setDiscovery] = React.useState([]);
  const [liveView,setLiveView] = useState(null);
  const [tick,setTick] = useState(0);
  useEffect(()=>{ const t=setInterval(()=>setTick(p=>p+1),1800); return()=>clearInterval(t); },[]);
  const openE   = events.filter(e=>e.status==="Open").length;
  const onlineC = cameras.filter(c=>c.status==="Online").length;
  const mrr     = tenants.filter(t=>t.status==="Active").reduce((a,t)=>{ const p=PLANS.find(p=>p.id===t.plan); return a+(p?p.monthly:0); },0);
  return(
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      {/* Hero Banner */}
      <div style={{background:"linear-gradient(135deg,#F0F4FB,#E8F0FD)",border:`1px solid ${T.accent}28`,borderRadius:10,padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:0,left:0,right:0,height:2,background:`linear-gradient(90deg,${T.accent},${T.blue},${T.green})`}}/>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <img src={AURAVEX_LOGO} alt="AURAVEX" style={{width:46,height:46,borderRadius:10,objectFit:"cover",border:`1px solid ${T.accent}55`,boxShadow:`0 0 18px ${T.accent}33`}}/>
          <div>
            <div style={{fontSize:18,fontWeight:800,background:`linear-gradient(90deg,${T.accent},#E8C060,${T.accent})`,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",letterSpacing:.5}}>AURAVEX GLOBAL AI</div>
            <div style={{fontSize:11,color:T.blue,marginTop:1,fontWeight:600}}>Engineering Intelligent Authority</div>
            <div style={{fontSize:10,color:T.muted,marginTop:2}}>AI-Powered Multi-Tenant Video Intelligence Platform</div>
          </div>
        </div>
        <div style={{display:"flex",gap:6}}>
          {/* removed */}
          {/* removed */}
          <Badge text="v4.2.1" color={T.muted} sm/>
        </div>
      </div>

      {/* KPIs Row 1 */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
        <KPI label="Live Cameras"    value={`${onlineC}/${cameras.length}`} sub={`${cameras.filter(c=>c.status==="Offline").length} offline - ${cameras.filter(c=>c.status==="Degraded").length} degraded`} icon="📹" color={T.blue}   trend={6}/>
        <KPI label="Open Events"     value={openE}                          sub={`${events.filter(e=>e.sev==="Critical"&&e.status==="Open").length} Critical`}                                             icon="🚨" color={T.red}/>
        <KPI label="Active Tenants"  value={tenants.filter(t=>t.status==="Active").length} sub={`${tenants.filter(t=>t.status==="Trial").length} trial - ${tenants.filter(t=>t.status==="Suspended").length} suspended`} icon="🏢" color={T.purple} trend={0}/>
        <KPI label="Platform MRR"    value={INR(mrr)} sub="Active subscriptions only" icon="💰" color={T.accent} trend={8}/>
      </div>

      {/* KPIs Row 2 */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
        <KPI label="Today Violations" value={violations.length}  sub="Traffic infractions" icon="🚦" color={T.orange}/>
        <KPI label="AI Detections"     value={events.length.toLocaleString("en-IN")}           sub="^ 14% vs yesterday"  icon="🤖" color={T.green} trend={14}/>
        <KPI label="CO2 Saved MTD"    value={`${(events.length*0.00004).toFixed(1)} t`}              sub="v 22% vs last month" icon="🌿" color={T.teal}  trend={22}/>
        <KPI label="Jetson Nodes"     value={cameras.length>0?Math.ceil(cameras.length/2):"--"}  sub={`${cameras.filter(c=>c.status==="Online").length} Online`}icon="⚡" color={T.blue}/>
      </div>

      {/* Event Trend + Severity */}
      <div style={{display:"grid",gridTemplateColumns:"3fr 2fr",gap:14}}>
        <Panel title="EVENT TREND -- LAST 24 HOURS" action={<Badge text="● LIVE" color={T.red} xs/>}>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={alertHours}>
              <defs>
                {[[T.red,"r"],[T.accent,"a"],[T.orange,"o"]].map(([c,id])=>(
                  <linearGradient key={id} id={"dg"+id} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={c} stopOpacity={.35}/>
                    <stop offset="95%" stopColor={c} stopOpacity={0}/>
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
              <XAxis dataKey="h" tick={{fill:T.muted,fontSize:9}} tickLine={false}/>
              <YAxis tick={{fill:T.muted,fontSize:9}} tickLine={false}/>
              <Tooltip contentStyle={{background:T.panel,border:`1px solid ${T.border}`,fontSize:10}}/>
              <Area type="monotone" dataKey="critical" stroke={T.red}    fill={`url(#dg${T.red})`}    strokeWidth={2} name="Critical"/>
              <Area type="monotone" dataKey="high"     stroke={T.accent} fill={`url(#dg${T.accent})`} strokeWidth={2} name="High"/>
              <Area type="monotone" dataKey="medium"   stroke={T.orange} fill={`url(#dg${T.orange})`} strokeWidth={2} name="Medium"/>
            </AreaChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="SEVERITY SPLIT">
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={[{n:"Critical",v:4},{n:"High",v:9},{n:"Medium",v:18},{n:"Low",v:12}]}
                cx="50%" cy="50%" innerRadius={44} outerRadius={68} paddingAngle={3} dataKey="v">
                {[T.red,T.accent,T.orange,T.green].map((c,i)=><Cell key={i} fill={c}/>)}
              </Pie>
              <Tooltip contentStyle={{background:T.panel,border:`1px solid ${T.border}`,fontSize:10}}/>
              <Legend iconType="circle" iconSize={7} wrapperStyle={{fontSize:10,color:T.muted}}/>
            </PieChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      {/* Traffic + Camera Grid */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
        <Panel title="TRAFFIC VIOLATIONS -- TODAY">
          <ResponsiveContainer width="100%" height={155}>
            <BarChart data={trafficHours}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
              <XAxis dataKey="h" tick={{fill:T.muted,fontSize:9}} tickLine={false}/>
              <YAxis tick={{fill:T.muted,fontSize:9}} tickLine={false}/>
              <Tooltip contentStyle={{background:T.panel,border:`1px solid ${T.border}`,fontSize:10}}/>
              <Bar dataKey="violations" fill={T.orange} radius={[3,3,0,0]} name="Violations"/>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="LIVE CAMERA GRID" action={<span style={{fontSize:10,color:T.muted}}>{onlineC} live</span>}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
            {cameras.map(cam=>{
              const c=({Online:T.green,Degraded:T.accent,Offline:T.red})[cam.status]||T.muted;
              return(
                <div key={cam.id} onClick={()=>setLiveView(cam)} style={{background:T.deep,borderRadius:7,padding:7,border:`1px solid ${c}25`,cursor:"pointer",transition:"all .12s"}}
                  onMouseEnter={e=>{e.currentTarget.style.border=`1px solid ${T.blue}55`;e.currentTarget.style.background=T.blueLo;}}
                  onMouseLeave={e=>{e.currentTarget.style.border=`1px solid ${c}25`;e.currentTarget.style.background=T.deep;}}>
                  <div style={{height:42,background:"linear-gradient(135deg,#E8EEF7,#D8E4F2)",borderRadius:5,marginBottom:5,display:"flex",alignItems:"center",justifyContent:"center",position:"relative",overflow:"hidden",border:`1px solid ${T.border}`}}>
                    {cam.status==="Online"&&<>
                      <span style={{fontSize:16}}>📹</span>
                      <span style={{position:"absolute",top:3,left:5,width:5,height:5,borderRadius:"50%",background:T.red,boxShadow:`0 0 ${tick%2===0?6:2}px ${T.red}`,transition:"box-shadow .8s"}}/>
                    </>}
                    {cam.status==="Offline"  && <span style={{fontSize:16,opacity:.25}}>📵</span>}
                    {cam.status==="Degraded" && <span style={{fontSize:14}}>⚠️</span>}
                    <span style={{position:"absolute",bottom:2,right:4,fontSize:7,fontFamily:T.mono,color:c,fontWeight:700}}>{cam.fps>0?`${cam.fps}fps`:"---"}</span>
                    <span style={{position:"absolute",top:3,right:4,fontSize:9,color:T.muted,opacity:.6}}>⛶</span>
                  </div>
                  <div style={{fontSize:9,fontWeight:700,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{cam.id}</div>
                  <Dot s={cam.status}/>
                </div>
              );
            })}
          </div>
        </Panel>
      </div>

      {/* Camera Live View Modal */}
      {liveView&&<CameraLiveModal cam={liveView} onClose={()=>setLiveView(null)} tick={tick}/>}

      {/* Tenant Overview with Avatars */}
      <Panel title="TENANT OVERVIEW -- SECTOR CLASSIFICATION">
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:10}}>
          {tenants.map(t=>(
            <div key={t.id} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6,padding:"10px 6px",background:T.deep,borderRadius:9,border:`1px solid ${sectorAvatar(t.sector)===AVATAR_GOV?T.blue+"30":T.teal+"30"}`,cursor:"default"}}>
              <img src={sectorAvatar(t.sector)} alt={t.name}
                style={{width:44,height:44,borderRadius:"50%",objectFit:"cover",objectPosition:"top",border:`2px solid ${sectorAvatar(t.sector)===AVATAR_GOV?T.blue+"66":T.teal+"66"}`,flexShrink:0}}/>
              <div style={{fontSize:9,fontWeight:700,color:T.text,textAlign:"center",lineHeight:1.3,overflow:"hidden",maxWidth:"100%",wordBreak:"break-word"}}>{t.name.split(" ").slice(0,2).join(" ")}</div>
              <Badge text={t.sector} xs color={sectorAvatar(t.sector)===AVATAR_GOV?T.blue:T.teal}/>
              <Dot s={t.status}/>
            </div>
          ))}
        </div>
      </Panel>

      {/* Recent Events */}
      <Panel title="RECENT EVENTS" action={<Badge text={`${openE} Open`} color={T.red} sm/>}>
        <DataTable dense cols={[
          {k:"id",   l:"Event ID",  r:v=><span style={{fontFamily:T.mono,color:T.blue,fontSize:10}}>{v}</span>},
          {k:"type", l:"Type",      r:v=><span style={{fontWeight:700}}>{v}</span>},
          {k:"sub",  l:"Sub-type",  r:v=><span style={{color:T.muted,fontSize:10}}>{v}</span>},
          {k:"camId",l:"Camera",    r:v=><span style={{fontFamily:T.mono,fontSize:10}}>{v}</span>},
          {k:"conf", l:"AI Conf.",  r:v=><span style={{fontFamily:T.mono,color:v>95?T.green:T.accent,fontWeight:700,fontSize:10}}>{v!=null&&v!==""?(v+"%"):"--"}</span>},
          {k:"sev",  l:"Severity",  r:v=><Badge text={v} color={sc(v)} xs/>},
          {k:"status",l:"Status",   r:v=><Dot s={v}/>},
          {k:"time", l:"Time",      r:v=><span style={{fontFamily:T.mono,color:T.muted,fontSize:10}}>{v}</span>},
        ]} rows={events.slice(0,5)}/>
      </Panel>
    </div>
  );
};

/* -- TENANTS + ONBOARDING WIZARD ---------------------------------- */
const ONBOARD_STEPS = ["Plan Confirm","Admin Setup","Site Config","Camera Reg.","Model Assign","Notifications","Go Live ✓"];

export const PageTenants = ({tenants,setTenants,plans,toast,sendNotification,logAudit}) => {
  const [sel,setSel]           = useState(null);
  const [showAdd,setShowAdd]   = useState(false);
  const [wizard,setWizard]     = useState(null);
  const [step,setStep]         = useState(0);
  const [editT,setEditT]       = useState(null);
  const [delT,setDelT]         = useState(null);
  const [form,setForm]         = useState({name:"",contact:"",sector:"Industrial",plan:"starter",billing:"monthly"});
  const ff = k => v => setForm(p=>({...p,[k]:v}));
  const ef = k => v => setEditT(p=>({...p,[k]:v}));

  const addTenant = () => {
    const pl = (plans||PLANS).find(p=>p.id===form.plan)||PLANS[0];
    const newT = {
      id:`T${String(tenants.length+1).padStart(3,"0")}`,name:form.name,contact:form.contact,
      sector:form.sector,plan:form.plan,status:"Trial",cameras:0,
      maxCam:pl.cameras,users:0,maxU:pl.users,storage:0,revenue:0,
      renewal:"",sites:0,onboarded:false,billing:form.billing,
    };
    setTenants(prev=>[...prev,newT]);
    apiPost('/api/tenants', tenantToAPI(newT)).then(saved=>{
      if(saved?.id) setTenants(p=>p.map(t=>t.id===newT.id?{...newT,...mapTenant(saved)}:t));
    }).catch(e=>toast("Save failed: "+e.message,"warn"));
    sendNotification&&sendNotification(form.contact,
      "Welcome to AURAVEX GLOBAL AI Platform",
      `Dear ${form.name},\n\nYour AURAVEX GLOBAL AI account has been created.\nPlan: ${pl.name}\nMonthly: ${INR(pl.monthly)}\n\nAn onboarding specialist will contact you within 24 hours.\n\nTeam AURAVEX GLOBAL AI`,
      ["email","sms"]
    );
    logAudit&&logAudit("TENANT_CREATED", form.name, "Info");
    setShowAdd(false); setForm({name:"",contact:"",sector:"Industrial",plan:"starter",billing:"monthly"});
    toast("Tenant created -- welcome notification sent");
  };

  const saveTenant = () => {
    setTenants(p=>p.map(t=>t.id===editT.id?{...editT}:t));
    apiPut(`/api/tenants/${editT.id}`, tenantToAPI(editT)).catch(e=>toast("Save failed: "+e.message,"warn"));
    sendNotification&&sendNotification(editT.contact,
      "AURAVEX GLOBAL AI: Account details updated",
      `Dear ${editT.name},\n\nYour account details have been updated by the administrator.\n\nIf you did not request these changes, please contact admin@auravex.ai immediately.\n\nTeam AURAVEX GLOBAL AI`,
      ["email"]
    );
    logAudit&&logAudit("TENANT_EDITED", editT.name, "Info");
    setEditT(null); toast(`${editT.name} updated -- notification sent`);
  };

  const deleteTenant = () => {
    setTenants(p=>p.filter(t=>t.id!==delT.id));
    apiDelete(`/api/tenants/${delT.id}`).catch(e=>toast("Delete failed: "+e.message,"warn"));
    sendNotification&&sendNotification(delT.contact,
      "AURAVEX GLOBAL AI: Account Terminated",
      `Dear ${delT.name},\n\nYour AURAVEX GLOBAL AI account has been terminated.\nData retained 30 days per policy.\n\nContact admin@auravex.ai for queries.\n\nTeam AURAVEX GLOBAL AI`,
      ["email","sms"]
    );
    logAudit&&logAudit("TENANT_DELETED", delT.name, "Critical");
    setDelT(null); toast(`${delT.name} deleted -- notification sent`, "warn");
  };

  const toggleStatus = id => {
    const t = tenants.find(x=>x.id===id);
    const newStatus = t?.status==="Active"?"Suspended":"Active";
    setTenants(p=>p.map(x=>x.id===id?{...x,status:newStatus}:x));
    apiPatch(`/api/tenants/${id}/status`,{status:newStatus}).catch(e=>toast("Status update failed: "+e.message,"warn"));
    sendNotification&&sendNotification(t.contact,
      `AURAVEX GLOBAL AI: Account ${newStatus}`,
      `Dear ${t.name},\n\nYour AURAVEX GLOBAL AI account has been ${newStatus.toLowerCase()} by the administrator.\n\n${newStatus==="Suspended"?"Please contact admin@auravex.ai to resolve any outstanding issues.":"You now have full access to the platform."}\n\nTeam AURAVEX GLOBAL AI`,
      ["email","sms"]
    );
    logAudit&&logAudit(`TENANT_${newStatus.toUpperCase()}`, t.name, newStatus==="Suspended"?"High":"Info");
    toast(`${t.name} ${newStatus} -- notification sent`);
  };

  const activateWizard = () => {
    setTenants(p=>p.map(t=>t.id===wizard.id?{...t,status:"Active",onboarded:true}:t));
    apiPatch(`/api/tenants/${wizard.id}/activate`,{}).catch(()=>{});
    sendNotification&&sendNotification(wizard.contact,
      "🚀 AURAVEX GLOBAL AI Platform is LIVE!",
      `Dear ${wizard.name},\n\nYour AURAVEX GLOBAL AI platform is now LIVE!\nAI inference is active on your cameras.\n\nLogin: https://auravex.ai/login\n\nTeam AURAVEX GLOBAL AI`,
      ["email","sms"]
    );
    logAudit&&logAudit("TENANT_ACTIVATED", wizard.name, "Info");
    setWizard(null); toast(`${wizard.name} is now LIVE ✓ -- notification sent`);
  };

  return(
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <PageHeader title="Tenant Management" sub="Multi-tenant isolation - INR subscriptions - Onboarding wizard - Quota enforcement - Auto-suspend">
        <Btn onClick={()=>setShowAdd(true)}>+ Add Tenant</Btn>
      </PageHeader>
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12}}>
        <KPI label="Total"     value={tenants.length}                              color={T.blue}   icon="🏢"/>
        <KPI label="Active"    value={tenants.filter(t=>t.status==="Active").length} color={T.green} icon="✅"/>
        <KPI label="Trial"     value={tenants.filter(t=>t.status==="Trial").length}  color={T.purple} icon="🔬"/>
        <KPI label="Suspended" value={tenants.filter(t=>t.status==="Suspended").length} color={T.red} icon="🔒"/>
        <KPI label="MRR"       value={INR(tenants.filter(t=>t.status==="Active").reduce((a,t)=>{const p=PLANS.find(p=>p.id===t.plan);return a+(p?p.monthly:0);},0))} color={T.accent} icon="💰"/>
      </div>

      {tenants.map(t=>{
        const pl=PLANS.find(p=>p.id===t.plan)||PLANS[0];
        return(
          <div key={t.id} onClick={()=>setSel(sel===t.id?null:t.id)}
            style={{background:sel===t.id?T.blueLo:T.card,border:`1px solid ${sel===t.id?T.blue+"55":T.border}`,borderRadius:10,padding:"13px 16px",cursor:"pointer",transition:"all .14s"}}>
            <div style={{display:"grid",gridTemplateColumns:"1.8fr 1fr 1fr 1fr 1fr 1fr auto",alignItems:"center",gap:12}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <img src={sectorAvatar(t.sector)} alt={t.sector}
                  style={{width:36,height:36,borderRadius:"50%",objectFit:"cover",objectPosition:"top",border:`2px solid ${t.sector==="Government"||t.sector==="Municipal"||t.sector==="Defence"||t.sector==="Police"?T.blue+"55":T.teal+"55"}`,flexShrink:0}}/>
                <div>
                  <div style={{fontWeight:800,fontSize:13}}>{t.name}</div>
                  <div style={{fontSize:10,color:T.muted,fontFamily:T.mono,marginTop:1}}>{t.id} - {t.sector}</div>
                </div>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:3}}>
                <Badge text={pl.name} color={pl.color} sm/>
                <span style={{fontSize:9,color:T.muted}}>{t.billing}</span>
              </div>
              <div>
                <div style={{fontSize:9,color:T.muted,marginBottom:2}}>Cameras</div>
                <div style={{fontFamily:T.mono,color:T.blue,fontWeight:700,fontSize:12}}>{t.cameras}/{t.maxCam}</div>
                <Prog val={t.cameras} max={t.maxCam} color={T.blue} h={3}/>
              </div>
              <div>
                <div style={{fontSize:9,color:T.muted,marginBottom:2}}>Storage</div>
                <div style={{fontFamily:T.mono,color:t.storage>85?T.red:T.text,fontWeight:700,fontSize:12}}>{t.storage}%</div>
                <Prog val={t.storage} h={3}/>
              </div>
              <div style={{fontSize:10,color:T.muted}}>MRR
                <div style={{color:T.accent,fontWeight:700,fontSize:12,fontFamily:T.mono}}>{INR(t.revenue)}</div>
              </div>
              <Dot s={t.status}/>
              <div style={{display:"flex",gap:5}} onClick={e=>e.stopPropagation()}>
                {!t.onboarded&&<Btn sm v="purple" onClick={()=>{setWizard(t);setStep(0);}}>Onboard</Btn>}
                <Btn sm v="ghost" onClick={()=>setEditT({...t})}>✏ Edit</Btn>
                <Btn sm v={t.status==="Active"?"danger":"success"} onClick={()=>toggleStatus(t.id)}>{t.status==="Active"?"Suspend":"Activate"}</Btn>
                <Btn sm v="danger" onClick={()=>setDelT(t)}>🗑</Btn>
              </div>
            </div>
            {sel===t.id&&(
              <div style={{marginTop:13,paddingTop:13,borderTop:`1px solid ${T.border}`}}>
                {/* -- Tenant Avatar + Title Banner -- */}
                <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:14,background:T.deep,borderRadius:10,padding:"12px 16px",border:`1px solid ${T.border}`}}>
                  <img src={sectorAvatar(t.sector)} alt={t.sector}
                    style={{width:72,height:72,borderRadius:12,objectFit:"cover",objectPosition:"top",border:`3px solid ${t.sector==="Government"||t.sector==="Municipal"||t.sector==="Defence"||t.sector==="Police"?T.blue+"66":T.teal+"66"}`,flexShrink:0,boxShadow:"0 4px 16px rgba(26,34,53,.18)"}}/>
                  <div style={{flex:1}}>
                    <div style={{fontSize:16,fontWeight:900,color:T.text}}>{t.name}</div>
                    <div style={{fontSize:11,color:T.muted,marginTop:2}}>{t.sector} - {t.contact}</div>
                    <div style={{display:"flex",gap:6,marginTop:8,flexWrap:"wrap"}}>
                      <Badge text={t.status} sm color={t.status==="Active"?T.green:t.status==="Trial"?T.accent:T.red}/>
                      <Badge text={t.sector==="Government"||t.sector==="Municipal"||t.sector==="Defence"||t.sector==="Police"?"🏛 Government":"🏢 Private"} sm color={t.sector==="Government"||t.sector==="Municipal"||t.sector==="Defence"||t.sector==="Police"?T.blue:T.teal}/>
                      <Badge text={pl.name} sm color={pl.color}/>
                      {t.onboarded&&<Badge text="✓ Onboarded" sm color={T.green}/>}
                    </div>
                  </div>
                  <div style={{textAlign:"right",fontSize:11,color:T.muted}}>
                    <div style={{fontSize:20,fontWeight:900,color:T.text,fontFamily:T.mono}}>{INR(t.revenue||0)}</div>
                    <div>MRR</div>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:10,marginBottom:12}}>
                  {[["Sites",t.sites,"📍",T.blue],["Users",`${t.users}/${t.maxU}`,"👥",T.purple],["Renewal",t.renewal||"--","📅",T.muted],["Plan",pl.name,"🎫",pl.color],["Billing",t.billing,"💳",T.teal]].map(([l,v,ic,c])=>(
                    <div key={l} style={{background:T.deep,borderRadius:8,padding:"9px 12px"}}>
                      <div style={{fontSize:9,color:T.muted,marginBottom:3}}>{ic} {l}</div>
                      <div style={{fontWeight:800,color:c,fontFamily:T.mono,fontSize:13}}>{v}</div>
                    </div>
                  ))}
                </div>
                <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
                  {["View Sites","Manage Users","Billing History","Reports","Audit Log"].map(a=><Btn key={a} sm v="ghost">{a}</Btn>)}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Edit Tenant Modal */}
      <Modal open={!!editT} title={`✏ Edit Tenant -- ${editT?.name}`} onClose={()=>setEditT(null)}>
        {editT&&<div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Grid cols={2} gap={12}>
            <Inp label="Organisation Name" value={editT.name}    onChange={ef("name")}    placeholder="Organisation name"/>
            <Inp label="Admin Email"       value={editT.contact} onChange={ef("contact")} placeholder="admin@org.in"/>
            <Sel label="Sector"            value={editT.sector}  onChange={ef("sector")}  options={["Government","Industrial","IT Park","Retail","Transport","Healthcare","Defence","Education","Smart City"]}/>
            <Sel label="Billing"           value={editT.billing} onChange={ef("billing")} options={[{v:"monthly",l:"Monthly"},{v:"yearly",l:"Yearly"},{v:"lifetime",l:"Lifetime"}]}/>
            <Sel label="Plan"              value={editT.plan}    onChange={ef("plan")}    options={(plans||PLANS).map(p=>({v:p.id,l:`${p.name} -- ${INR(p.monthly)}/mo`}))}/>
            <Sel label="Status"            value={editT.status}  onChange={ef("status")}  options={["Active","Trial","Suspended"]}/>
          </Grid>
          <AlertBox type="info">i️ A notification email will be sent to {editT.contact} on save.</AlertBox>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn v="ghost" onClick={()=>setEditT(null)}>Cancel</Btn>
            <Btn onClick={saveTenant} disabled={!editT.name}>💾 Save & Notify</Btn>
          </div>
        </div>}
      </Modal>

      {/* Delete Tenant Confirm */}
      <Modal open={!!delT} title="⚠️ Confirm Delete Tenant" onClose={()=>setDelT(null)}>
        {delT&&<div style={{display:"flex",flexDirection:"column",gap:12}}>
          <AlertBox type="error">🗑 Permanently delete <strong>{delT.name}</strong>? This removes all cameras, data and access. Cannot be undone.</AlertBox>
          <AlertBox type="info">SMS + Email termination notice will be sent to {delT.contact}.</AlertBox>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn v="ghost" onClick={()=>setDelT(null)}>Cancel</Btn>
            <Btn v="danger" onClick={deleteTenant}>🗑 Delete & Notify</Btn>
          </div>
        </div>}
      </Modal>

      {/* Add Tenant Modal */}
      <Modal open={showAdd} title="Register New Tenant" onClose={()=>setShowAdd(false)}>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Inp label="Organisation Name" value={form.name}    onChange={ff("name")}    placeholder="e.g. Pune Smart City Corporation"/>
          <Inp label="Admin Email"       value={form.contact} onChange={ff("contact")} placeholder="admin@organisation.in" type="email"/>
          <Grid cols={2} gap={12}>
            <Sel label="Sector"  value={form.sector}  onChange={ff("sector")}  options={["Government","Industrial","IT Park","Retail","Transport","Healthcare","Defence","Education","Smart City"]}/>
            <Sel label="Billing" value={form.billing} onChange={ff("billing")} options={[{v:"monthly",l:"Monthly"},{v:"yearly",l:"Yearly (save ~15%)"},{v:"lifetime",l:"Lifetime"}]}/>
          </Grid>
          <Sel label="Subscription Plan" value={form.plan} onChange={ff("plan")} options={(plans||PLANS).map(p=>({v:p.id,l:`${p.name} -- ${INR(p.monthly)}/mo - ${p.cameras} cameras`}))}/>
          {(()=>{ const p=PLANS.find(pl=>pl.id===form.plan); return p&&(
            <div style={{background:T.deep,borderRadius:8,padding:12,display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,fontSize:11}}>
              {[["Monthly",INR(p.monthly)],["Yearly",INR(p.yearly)],["Lifetime",INR(p.lifetime)],["Cameras",p.cameras],["Storage",p.storage],["SLA",p.sla]].map(([l,v])=>(
                <div key={l}><span style={{color:T.muted}}>{l}: </span><span style={{fontWeight:700}}>{v}</span></div>
              ))}
            </div>
          );})()}
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn v="ghost" onClick={()=>setShowAdd(false)}>Cancel</Btn>
            <Btn onClick={addTenant} disabled={!form.name||!form.contact}>Create & Start Onboarding</Btn>
          </div>
        </div>
      </Modal>

      {/* Onboarding Wizard */}
      <Modal open={!!wizard} title={`Client Onboarding -- ${wizard?.name}`} onClose={()=>setWizard(null)} wide>
        {wizard&&(
          <div>
            {/* Stepper */}
            <div style={{display:"flex",alignItems:"center",marginBottom:20,overflowX:"auto",paddingBottom:4}}>
              {ONBOARD_STEPS.map((s,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",flex:i<ONBOARD_STEPS.length-1?"1":"0"}}>
                  <div onClick={()=>setStep(i)} style={{display:"flex",flexDirection:"column",alignItems:"center",cursor:"pointer",minWidth:52}}>
                    <div style={{width:28,height:28,borderRadius:"50%",background:i<step?T.green:i===step?T.accent:T.border,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:i<=step?"#000":T.muted,transition:"background .2s"}}>{i<step?"✓":i+1}</div>
                    <div style={{fontSize:8,color:i===step?T.accent:T.muted,marginTop:3,textAlign:"center",fontWeight:600,lineHeight:1.2,maxWidth:52}}>{s}</div>
                  </div>
                  {i<ONBOARD_STEPS.length-1&&<div style={{flex:1,height:2,background:i<step?T.green:T.border,margin:"0 3px",marginBottom:14,transition:"background .2s"}}/>}
                </div>
              ))}
            </div>
            {/* Step panels */}
            <div style={{background:T.deep,borderRadius:9,padding:16,minHeight:190}}>
              {step===0&&(()=>{const p=PLANS.find(pl=>pl.id===wizard.plan)||PLANS[0];return(
                <div><div style={{fontWeight:700,fontSize:13,marginBottom:10}}>Step 1 -- Plan Confirmation & Contract</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:7,fontSize:12}}>
                  {[["Plan",p.name],["Monthly",INR(p.monthly)],["Yearly",INR(p.yearly)],["Billing",wizard.billing],["Cameras",p.cameras],["Users",p.users],["Storage",p.storage],["Retention",p.retention],["SLA",p.sla],["Support",p.support],["Jetson",p.jetson?"✓":"✗"],["Custom Models",p.custom?"✓":"✗"]].map(([l,v])=>(
                    <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:`1px solid ${T.border}`}}>
                      <span style={{color:T.muted}}>{l}</span>
                      <span style={{fontWeight:600,color:String(v).startsWith("✓")?T.green:String(v).startsWith("✗")?T.red:T.text}}>{v}</span>
                    </div>
                  ))}
                </div></div>
              );})()}
              {step===1&&<div><div style={{fontWeight:700,fontSize:13,marginBottom:10}}>Step 2 -- Admin User Account</div>
                <Grid cols={2} gap={12}><Inp label="Admin Full Name" value="" onChange={()=>{}} placeholder="Rajesh Sharma"/>
                  <Inp label="Email" value={wizard.contact} onChange={()=>{}} placeholder="admin@org.in"/>
                  <Inp label="Mobile (+91)" value="" onChange={()=>{}} placeholder="+91 9XXXXXXXXX"/>
                  <Sel label="MFA Method" value="TOTP" onChange={()=>{}} options={["TOTP App","SMS OTP","Email OTP","Hardware Key"]}/>
                </Grid>
                <div style={{marginTop:10}}><AlertBox type="success">✓ Welcome email + MFA setup link sent automatically after Go Live.</AlertBox></div>
              </div>}
              {step===2&&<div><div style={{fontWeight:700,fontSize:13,marginBottom:10}}>Step 3 -- Site Configuration</div>
                <Grid cols={2} gap={12}><Inp label="Site Name" value="" onChange={()=>{}} placeholder="Main Campus Gate"/>
                  <Inp label="City" value="" onChange={()=>{}} placeholder="Pune, Maharashtra"/>
                  <Sel label="Site Type" value="Industrial Entry" onChange={()=>{}} options={["Traffic Intersection","Industrial Entry","Factory Floor","IT Campus","Mall","Hospital","Transport Hub","Smart City Zone"]}/>
                  <Sel label="Jetson Node" value={(jetsons||[])[0]?.id||""} onChange={()=>{}} options={(jetsons||[]).map(j=>({v:j.id,l:`${j.id} -- ${j.model}`}))}/>
                </Grid>
              </div>}
              {step===3&&<div><div style={{fontWeight:700,fontSize:13,marginBottom:10}}>Step 4 -- Camera Registration</div>
                <Grid cols={2} gap={12}><Inp label="Camera Name" value="" onChange={()=>{}} placeholder="Gate 1 Inbound PTZ"/>
                  <Inp label="RTSP / IP" value="" onChange={()=>{}} placeholder="rtsp://192.168.1.100:554"/>
                  <Sel label="Brand" value="Hikvision" onChange={()=>{}} options={["Hikvision","Dahua","Axis","Hanwha","CP Plus","Bosch"]}/>
                  <Sel label="Resolution" value="4K" onChange={()=>{}} options={["1080p","2K","4K","8K"]}/>
                </Grid>
                <div style={{marginTop:10}}><AlertBox>RTSP stream validated by Jetson within ~2-4 min after Go Live.</AlertBox></div>
              </div>}
              {step===4&&<div><div style={{fontWeight:700,fontSize:13,marginBottom:10}}>Step 5 -- AI Model Assignment</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  {(models||[]).filter(m=>m.type==="Standard"||m.type==="standard"||!m.type||m.cat).map(m=>(
                    <div key={m.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:T.panel,borderRadius:7,padding:"8px 11px",border:`1px solid ${T.border}`}}>
                      <div><div style={{fontSize:11,fontWeight:700}}>{m.name}</div><div style={{fontSize:9,color:T.muted}}>{INR(m.price)}/cam/mo - {m.acc}%</div></div>
                      <Badge text={m.cat} color={T.blue} xs/>
                    </div>
                  ))}
                </div>
              </div>}
              {step===5&&<div><div style={{fontWeight:700,fontSize:13,marginBottom:10}}>Step 6 -- Notification Setup</div>
                <Grid cols={2} gap={12}><Inp label="Email Recipients" value="" onChange={()=>{}} placeholder="admin@org.in, ops@org.in"/>
                  <Inp label="WhatsApp (+91)" value="" onChange={()=>{}} placeholder="+91 9XXXXXXXXX"/>
                  <Sel label="SMS Gateway" value="MSG91" onChange={()=>{}} options={["MSG91","Twilio","Kaleyra","Exotel"]}/>
                  <Sel label="Min Alert Severity" value="High" onChange={()=>{}} options={["Critical Only","High & Critical","Medium+","All"]}/>
                </Grid>
              </div>}
              {step===6&&<div style={{textAlign:"center",padding:"24px 0"}}>
                <div style={{fontSize:50,marginBottom:12}}>🚀</div>
                <div style={{fontSize:16,fontWeight:800,color:T.green,marginBottom:8}}>Ready to Go Live!</div>
                <div style={{fontSize:12,color:T.muted,lineHeight:1.8,maxWidth:400,margin:"0 auto"}}>All 6 steps complete. Click <strong style={{color:T.text}}>Activate Tenant</strong> to bring this client live. AI inference begins within ~5 minutes.</div>
              </div>}
            </div>
            {/* Wizard nav */}
            <div style={{display:"flex",justifyContent:"space-between",marginTop:16}}>
              <Btn v="muted" onClick={()=>setStep(Math.max(0,step-1))} disabled={step===0}>&lt;- Back</Btn>
              <div style={{display:"flex",gap:8}}>
                <Btn v="ghost" onClick={()=>setWizard(null)}>Save & Exit</Btn>
                {step<ONBOARD_STEPS.length-1
                  ?<Btn onClick={()=>setStep(step+1)}>Next Step -></Btn>
                  :<Btn v="success" onClick={activateWizard}>🚀 Activate Tenant</Btn>}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

/* -- SITES & CAMERAS ---------------------------------------------- */
export const PageCameras = ({cameras,setCameras,tenants,sites,jetsons,models,toast,sendNotification,logAudit}) => {
  const [viewMode,setViewMode] = useState("grid");
  const [fStatus,setFStatus]   = useState("All");
  const [fSite,setFSite]       = useState("All");
  const [sel,setSel]           = useState(null);
  const [showAdd,setShowAdd]   = useState(false);
  const [showEdit,setShowEdit] = useState(null);
  const [delCam,setDelCam]     = useState(null);
  const [tick,setTick]         = useState(0);
  const [liveView,setLiveView]  = useState(null);
  const [form,setForm]         = useState({name:"",siteId:"",ip:"",brand:"Hikvision",type:"Fixed",res:"1080p",fps:"25",zone:""});
  const ff = k => v => setForm(p=>({...p,[k]:v}));
  const [showAddSite,setShowAddSite] = useState(false);
  const [siteForm,setSiteForm] = useState({name:"",city:"",type:"Industrial Entry",tenantId:"",jetsonId:"",address:"",contactName:"",contactPhone:"",lat:"",lng:""});
  const sf = k => v => setSiteForm(p=>({...p,[k]:v}));
  const [showDiscover,setShowDiscover] = useState(false);
  const [discovering,setDiscovering]   = useState(false);
  const [discovered,setDiscovered]     = useState([]);
  const [netWarnings,setNetWarnings]   = useState([]);
  const [netSuggestions,setNetSuggestions] = useState([]);
  const [netInfo,setNetInfo]           = useState(null);
  useEffect(()=>{ const t=setInterval(()=>setTick(p=>p+1),1800); return()=>clearInterval(t); },[]);

  const [localSites, setLocalSites] = useState(sites || SITES_DATA);
  useEffect(()=>{ if(sites) setLocalSites(sites); },[sites]);
  const sitesData = localSites;
  const TNAME = sitesData.reduce((a,s)=>{ const t=(tenants||[]).find(x=>x.id===s.tenantId); return{...a,[s.id]:t?.name||s.tenantId}; },{});
  let filtered = cameras;
  if(fStatus!=="All") filtered=filtered.filter(c=>c.status===fStatus);
  if(fSite!=="All")   filtered=filtered.filter(c=>(c.siteId||c.site_id)===fSite);

  const reboot    = id => { setCameras(p=>p.map(c=>c.id===id?{...c,status:"Online",health:Math.min(100,c.health+20)}:c)); apiPatch(`/api/cameras/${id}/reboot`,{}).catch(()=>{/* ignore reboot errors */}); logAudit&&logAudit("CAMERA_REBOOTED",id,"Info"); toast(`${id} rebooted`); };
  const toggleRec = id => { setCameras(p=>p.map(c=>c.id===id?{...c,recording:!c.recording}:c)); apiPatch(`/api/cameras/${id}/recording`,{}).catch(()=>{}); toast("Recording toggled"); };
  const saveEdit  = () => {
    setCameras(p=>p.map(c=>c.id===showEdit.id?showEdit:c));
    apiPut(`/api/cameras/${showEdit.id}`, cameraToAPI(showEdit)).catch(e=>toast("Save failed: "+e.message,"warn"));
    logAudit&&logAudit("CAMERA_EDITED",showEdit.id,"Info");
    setShowEdit(null); toast(`${showEdit.id} updated`);
  };
  const deleteCam = () => {
    setCameras(p=>p.filter(c=>c.id!==delCam.id));
    apiDelete(`/api/cameras/${delCam.id}`).catch(e=>toast("Delete failed: "+e.message,"warn"));
    logAudit&&logAudit("CAMERA_DELETED",delCam.id,"High");
    setDelCam(null); toast(`${delCam.id} deleted`,"warn");
  };
  const addCam    = () => {
    const id=`CAM-${String(cameras.length+1).padStart(3,"0")}`;
    const newCam={id,...form,fps:parseInt(form.fps)||25,health:100,models:[],status:"Online",recording:true,retention:"30d",alerts:0};
    setCameras(p=>[...p,newCam]);
    apiPost('/api/cameras', cameraToAPI(newCam)).then(saved=>{
      if(saved?.id) setCameras(p=>p.map(c=>c.id===newCam.id?{...newCam,...mapCamera(saved)}:c));
    }).catch(e=>toast("Save failed: "+e.message,"warn"));
    logAudit&&logAudit("CAMERA_ADDED",id,"Info");
    setShowAdd(false); setForm({name:"",siteId:"",ip:"",brand:"Hikvision",type:"Fixed",res:"1080p",fps:"25",zone:""});
    toast(`${id} registered -- provisioning...`);
  };

  const addSite = () => {
    const payload = {
      name:siteForm.name, city:siteForm.city, type:siteForm.type,
      tenant_id:siteForm.tenantId, jetson_id:siteForm.jetsonId,
      address:siteForm.address, contact_name:siteForm.contactName,
      contact_phone:siteForm.contactPhone, lat:siteForm.lat, lng:siteForm.lng,
      status:'Active'
    };
    apiPost('/api/sites', payload).then(saved => {
      if (saved && saved.id) {
        setLocalSites(prev => [...prev, { ...saved, cameras:0, zones:0 }]);
      }
    }).catch(e=>toast("Save failed: "+e.message,"warn"));
    logAudit&&logAudit("SITE_ADDED", siteForm.name, "Info");
    setShowAddSite(false);
    setSiteForm({name:"",city:"",type:"Industrial Entry",tenantId:"",jetsonId:"",address:"",contactName:"",contactPhone:"",lat:"",lng:""});
    toast(`Site ${siteForm.name} registered`);
  };

  const discoverDevices = async () => {
    setDiscovering(true); setDiscovered([]); setNetWarnings([]); setNetSuggestions([]); setNetInfo(null);
    try {
      // Detect host subnet: use browser's origin hostname (the real LAN IP or localhost)
      // If localhost, try common private subnets; otherwise derive from actual IP
      let subnet = '';
      const host = window.location.hostname;
      if (host && host.match(/^\d+\.\d+\.\d+\.\d+$/)) {
        subnet = host.split('.').slice(0,3).join('.');
      } else {
        subnet = '192.168.0'; // default for local dev
      }
      const res = await apiFetch(`/api/discover?subnet=${subnet}`);
      setDiscovered(res?.devices || []);
      setNetWarnings(res?.warnings || []);
      setNetSuggestions(res?.suggestions || []);
      setNetInfo({ interfaces: res?.interfaces, gateways: res?.gateways, subnetsScanned: res?.subnetsScanned, hostSubnet: subnet });
      if(!res?.devices?.length) toast("No new devices found on network","warn");
      else if(res?.warnings?.length) toast(`Found ${res.devices.length} device(s) with ${res.warnings.length} network warning(s)`,"warn");
      else toast(`Found ${res.devices.length} device(s) — network OK`);
    } catch(e) {
      toast("Discovery failed: "+e.message,"warn");
    }
    setDiscovering(false);
  };

  const prefillCamera = (dev) => {
    setForm(p=>({...p, name:dev.hostname||`Camera-${dev.ip.split('.').pop()}`, ip:`rtsp://${dev.ip}:${dev.port}/Streaming/Channels/101`}));
    setShowDiscover(false); setShowAdd(true);
    toast(`Pre-filled camera from ${dev.ip}`);
  };

  const addJetsonFromDiscovery = async (dev) => {
    try {
      const jetsonData = {
        name: dev.hostname || `Jetson-${dev.ip.split('.').pop()}`,
        ip: dev.ip,
        model: 'Orin Nano',
        jetpack: '6.0',
        status: 'Online',
        max_cameras: 8,
        mac: dev.mac || '',
        port: dev.port || 8080,
      };
      await apiPost('/api/jetsons', jetsonData);
      logAudit&&logAudit("JETSON_ADDED_DISCOVERY", dev.ip, "Info");
      setShowDiscover(false);
      toast(`⚡ Jetson ${dev.ip} registered — view in Jetson Nodes tab`);
    } catch(e) {
      toast("Failed to add Jetson: " + e.message, "warn");
    }
  };

  return(
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <PageHeader title="Sites - Zones - Cameras" sub="RTSP/ONVIF - Edge node binding - AI model assignment - Health monitoring - Evidence recording">
        <Btn v="ghost" sm onClick={()=>setViewMode(v=>v==="grid"?"table":"grid")}>{viewMode==="grid"?"☰ Table":"[+] Grid"}</Btn>
        <Btn v="teal" sm onClick={()=>{setShowDiscover(true);discoverDevices();}}>🔍 Discover Devices</Btn>
        <Btn v="blue" onClick={()=>setShowAddSite(true)}>📍 Add Site</Btn>
        <Btn onClick={()=>setShowAdd(true)}>📹 Add Camera</Btn>
      </PageHeader>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
        <KPI label="Total"         value={cameras.length}                              sub={`${new Set(cameras.map(c=>c.siteId)).size} sites`} icon="📹" color={T.blue}/>
        <KPI label="Online"        value={cameras.filter(c=>c.status==="Online").length} sub={Math.round(cameras.filter(c=>c.status==="Online").length/cameras.length*100)+"% avail."} icon="✅" color={T.green}/>
        <KPI label="Degraded/Offline" value={`${cameras.filter(c=>c.status==="Degraded").length}/${cameras.filter(c=>c.status==="Offline").length}`} icon="⚠️" color={T.accent}/>
        <KPI label="Recording"     value={cameras.filter(c=>c.recording).length}       sub={`${cameras.filter(c=>!c.recording).length} paused`} icon="🔴" color={T.red}/>
      </div>
      {/* Filters */}
      <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
        <span style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:1}}>STATUS:</span>
        {["All","Online","Degraded","Offline"].map(s=><Pill key={s} label={s} active={fStatus===s} onClick={()=>setFStatus(s)} color={sc(s)||T.blue}/>)}
        <div style={{width:1,height:16,background:T.border,margin:"0 4px"}}/>
        <span style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:1}}>SITE:</span>
        {["All",...sitesData.map(s=>s.id)].map(s=><Pill key={s} label={s} active={fSite===s} onClick={()=>setFSite(s)} color={T.purple}/>)}
      </div>
      {/* Grid view */}
      {viewMode==="grid"&&(
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
          {filtered.map(cam=>{
            const c=sc(cam.status);
            return(
              <div key={cam.id}
                onClick={()=>setSel(sel===cam.id?null:cam.id)}
                onDoubleClick={()=>setLiveView(cam)}
                style={{background:sel===cam.id?"#0B1E3C":T.card,border:`1px solid ${sel===cam.id?T.blue+"55":c+"30"}`,borderRadius:10,padding:13,cursor:"pointer",transition:"all .14s",position:"relative",overflow:"hidden"}}>
                <div style={{position:"absolute",top:0,left:0,right:0,height:2,background:c}}/>
                {/* Camera thumbnail -- live feed */}
                <div style={{height:68,background:"linear-gradient(135deg,#0A1628,#0D1F3C)",borderRadius:7,marginBottom:10,display:"flex",alignItems:"center",justifyContent:"center",position:"relative",border:`1px solid ${T.border}`,overflow:"hidden"}}>
                  {cam.status==="Online"&&<>
                    <img src={getStreamUrl(cam).type==='snapshot'?`${getStreamUrl(cam).url}${getStreamUrl(cam).url.includes('?')?'&':'?'}t=${tick}`:getStreamUrl(cam).url}
                      alt={cam.name} style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover"}}
                      onError={e=>{e.target.style.opacity='0';if(e.target.parentNode){var d=e.target.parentNode.querySelector('.cam-offline');if(!d){d=document.createElement('div');d.className='cam-offline';d.style.cssText='position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);color:#C48A0E;font-size:12px;text-align:center';d.innerHTML='\u26A0 Camera Offline<br><span style=color:#7A90B0;font-size:10px>Edge agent not responding</span>';e.target.parentNode.style.position='relative';e.target.parentNode.appendChild(d);}}}} onLoad={e=>{e.target.style.opacity='1';var d=e.target.parentNode&&e.target.parentNode.querySelector('.cam-offline');if(d)d.remove();}}/>
                    <span style={{position:"absolute",top:4,left:6,width:6,height:6,borderRadius:"50%",background:T.red,boxShadow:`0 0 ${tick%2===0?7:2}px ${T.red}`,transition:"box-shadow .8s",zIndex:2}}/>
                    <span style={{fontSize:22,opacity:.15,zIndex:1}}>📹</span>
                    {cam.recording&&<div style={{position:"absolute",bottom:4,left:7,display:"flex",alignItems:"center",gap:3,zIndex:2}}><span style={{width:5,height:5,borderRadius:"50%",background:T.red}}/><span style={{fontSize:7,color:T.red,fontWeight:700,fontFamily:T.mono}}>REC</span></div>}
                  </>}
                  {cam.status==="Offline"  &&<span style={{fontSize:22,opacity:.22}}>📵</span>}
                  {cam.status==="Degraded" &&<span style={{fontSize:22}}>⚠️</span>}
                  <div style={{position:"absolute",top:4,right:6,fontSize:8,fontFamily:T.mono,color:c,fontWeight:700}}>{cam.fps>0?`${cam.fps}fps`:"---"} - {cam.res}</div>
                  {/* Maximize button -- single click opens live modal */}
                  <button
                    onClick={e=>{ e.stopPropagation(); setLiveView(cam); }}
                    title="Open Live View"
                    style={{position:"absolute",bottom:4,right:5,width:20,height:20,borderRadius:4,
                      background:`rgba(10,30,70,.75)`,border:`1px solid ${T.blue}55`,
                      display:"flex",alignItems:"center",justifyContent:"center",
                      cursor:"pointer",fontSize:11,color:T.blue,lineHeight:1,backdropFilter:"blur(4px)",
                      transition:"all .12s"}}
                    onMouseEnter={e=>{ e.currentTarget.style.background=T.blue; e.currentTarget.style.color="#fff"; }}
                    onMouseLeave={e=>{ e.currentTarget.style.background="rgba(10,30,70,.75)"; e.currentTarget.style.color=T.blue; }}>
                    ⛶
                  </button>
                </div>
                <div style={{fontSize:11,fontWeight:800}}>{cam.id} -- {cam.name}</div>
                <div style={{fontSize:10,color:T.muted,marginTop:1}}>{cam.brand} - {cam.type} - {TNAME[cam.siteId]||cam.siteId}</div>
                <div style={{display:"flex",gap:3,marginTop:7,flexWrap:"wrap",alignItems:"center"}}>
                  {cam.models.slice(0,2).map(m=><Badge key={m} text={m.split(" ")[0]} xs color={T.teal}/>)}
                  {cam.models.length>2&&<span style={{fontSize:9,color:T.muted}}>+{cam.models.length-2}</span>}
                  <Dot s={cam.status}/>
                </div>
                {cam.status!=="Offline"&&<div style={{marginTop:8}}><Prog val={cam.health} color={T.green} h={4}/></div>}
                {sel===cam.id&&(
                  <div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${T.border}`}}>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4,marginBottom:8,fontSize:10}}>
                      {[["IP",cam.ip],["Site",cam.siteId],["Health",cam.health+"%"],["Retention",cam.retention],["Alerts",cam.alerts],["Rec",cam.recording?"Active":"Paused"]].map(([l,v])=>(
                        <div key={l}><span style={{color:T.muted}}>{l}: </span><span style={{fontWeight:600}}>{v}</span></div>
                      ))}
                    </div>
                    <div style={{display:"flex",gap:5,flexWrap:"wrap"}} onClick={e=>e.stopPropagation()}>
                      <Btn sm v="ghost" onClick={()=>setShowEdit(cam)}>✏ Edit</Btn>
                      <Btn sm v="blue"  onClick={()=>reboot(cam.id)}>🔄 Reboot</Btn>
                      <Btn sm v={cam.recording?"danger":"success"} onClick={()=>toggleRec(cam.id)}>{cam.recording?"Stop":"Rec"}</Btn>
                      <Btn sm v="danger" onClick={()=>setDelCam(cam)}>🗑</Btn>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* Table view */}
      {viewMode==="table"&&(
        <Panel title={`${filtered.length} CAMERAS`}>
          <DataTable dense cols={[
            {k:"id",      l:"ID",       r:v=><span style={{fontFamily:T.mono,color:T.blue,fontSize:10}}>{v}</span>},
            {k:"name",    l:"Name",     r:v=><span style={{fontWeight:700}}>{v}</span>},
            {k:"siteId",  l:"Site",     r:v=><span style={{fontSize:10,color:T.muted}}>{TNAME[v]||v}</span>},
            {k:"brand",   l:"Brand"},
            {k:"type",    l:"Type",     r:v=><Badge text={v} xs color={T.purple}/>},
            {k:"res",     l:"Res",      r:v=><span style={{fontFamily:T.mono,fontSize:10}}>{v}</span>},
            {k:"health",  l:"Health",   r:v=><div style={{minWidth:70}}><span style={{fontSize:9,fontFamily:T.mono,color:v>70?T.green:T.accent}}>{v}%</span><Prog val={v} color={T.green} h={3}/></div>},
            {k:"models",  l:"AI Models",r:v=><div style={{display:"flex",gap:2}}>{v.slice(0,2).map(m=><Badge key={m} text={m.split(" ")[0]} xs color={T.teal}/>)}</div>},
            {k:"recording",l:"Rec",     r:v=><Badge text={v?"REC":"OFF"} color={v?T.red:T.muted} xs/>},
            {k:"status",  l:"Status",   r:v=><Dot s={v}/>},
            {k:"alerts",  l:"Alerts",   r:v=><span style={{fontFamily:T.mono,color:v>15?T.red:v>5?T.accent:T.text,fontWeight:700,fontSize:10}}>{v}</span>},
            {k:"_act",l:"Actions",r:(v,row)=><div style={{display:"flex",gap:4}}><Btn sm v="ghost" onClick={()=>setShowEdit(row)}>Edit</Btn><Btn sm v="blue" onClick={()=>reboot(v)}>Reboot</Btn><Btn sm v="danger" onClick={()=>setDelCam(row)}>🗑</Btn></div>},
          ]} rows={filtered}/>
        </Panel>
      )}
      {/* Add camera modal */}
      <Modal open={showAdd} title="Register New Camera" onClose={()=>setShowAdd(false)}>
        <Grid cols={2} gap={12}>
          <Inp label="Camera Name"   value={form.name}   onChange={ff("name")}  placeholder="Gate 1 PTZ"/>
          <Inp label="RTSP / IP"     value={form.ip}     onChange={ff("ip")}    placeholder="rtsp://192.168.1.100:554"/>
          <Sel label="Site"          value={form.siteId} onChange={ff("siteId")} options={[{v:"",l:"-- No Site --"},...sitesData.map(s=>({v:s.id,l:s.name}))]}/>
          <Inp label="Zone"          value={form.zone}   onChange={ff("zone")}  placeholder="Zone A -- Entry Gate"/>
          <Sel label="Brand"         value={form.brand}  onChange={ff("brand")} options={["Hikvision","Dahua","Axis","Hanwha","CP Plus","Bosch"]}/>
          <Sel label="Type"          value={form.type}   onChange={ff("type")}  options={["Fixed","PTZ","360deg","Thermal","Fisheye","Dome"]}/>
          <Sel label="Resolution"    value={form.res}    onChange={ff("res")}   options={["720p","1080p","2K","4K","8K"]}/>
          <Sel label="FPS"           value={form.fps}    onChange={ff("fps")}    options={["10","15","20","25","30"]}/>
        </Grid>
        <div style={{margin:"12px 0"}}><AlertBox>Auto-provisioning: RTSP stream validated and AI pipeline assigned by Jetson within ~2-4 min.</AlertBox></div>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
          <Btn v="ghost" onClick={()=>setShowAdd(false)}>Cancel</Btn>
          <Btn onClick={addCam} disabled={!form.name||!form.ip}>📹 Register Camera</Btn>
        </div>
      </Modal>
      {/* Edit camera modal */}
      <Modal open={!!showEdit} title={`Edit -- ${showEdit?.id}`} onClose={()=>setShowEdit(null)}>
        {showEdit&&<div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Grid cols={2} gap={12}>
            <Inp label="Name"       value={showEdit.name}      onChange={v=>setShowEdit(p=>({...p,name:v}))}      placeholder="Camera name"/>
            <Inp label="IP"         value={showEdit.ip}        onChange={v=>setShowEdit(p=>({...p,ip:v}))}        placeholder="IP address"/>
            <Sel label="Retention"  value={showEdit.retention} onChange={v=>setShowEdit(p=>({...p,retention:v}))} options={["7d","14d","30d","60d","90d","1yr","3yr"]}/>
            <Sel label="Resolution" value={showEdit.res}       onChange={v=>setShowEdit(p=>({...p,res:v}))}       options={["720p","1080p","2K","4K","8K"]}/>
            <Sel label="FPS"        value={String(showEdit.fps||25)} onChange={v=>setShowEdit(p=>({...p,fps:parseInt(v)}))} options={["10","15","20","25","30"]}/>
            <Sel label="Status"     value={showEdit.status}    onChange={v=>setShowEdit(p=>({...p,status:v}))}    options={["Online","Degraded","Offline"]}/>
            <Sel label="Brand"      value={showEdit.brand}     onChange={v=>setShowEdit(p=>({...p,brand:v}))}     options={["Hikvision","Dahua","Axis","Hanwha","CP Plus","Bosch","Uniview"]}/>
            <Inp label="Zone"       value={showEdit.zone||""}  onChange={v=>setShowEdit(p=>({...p,zone:v}))}      placeholder="Zone A"/>
          </Grid>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn v="ghost" onClick={()=>setShowEdit(null)}>Cancel</Btn>
            <Btn onClick={saveEdit}>💾 Save Changes</Btn>
          </div>
        </div>}
      </Modal>

      {/* Delete camera confirm */}
      <Modal open={!!delCam} title="⚠️ Delete Camera" onClose={()=>setDelCam(null)}>
        {delCam&&<div style={{display:"flex",flexDirection:"column",gap:12}}>
          <AlertBox type="error">🗑 Delete <strong>{delCam.id} -- {delCam.name}</strong>? All recordings and AI data for this camera will be removed. Cannot be undone.</AlertBox>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn v="ghost" onClick={()=>setDelCam(null)}>Cancel</Btn>
            <Btn v="danger" onClick={deleteCam}>🗑 Confirm Delete</Btn>
          </div>
        </div>}
      </Modal>

      {/* Add Site modal */}
      <Modal open={showAddSite} title="Register New Site" onClose={()=>setShowAddSite(false)}>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Grid cols={2} gap={12}>
            <Inp label="Site Name"        value={siteForm.name}         onChange={sf("name")}         placeholder="e.g. Main Campus Gate"/>
            <Inp label="City"             value={siteForm.city}         onChange={sf("city")}         placeholder="e.g. Pune, Maharashtra"/>
            <Sel label="Site Type"        value={siteForm.type}         onChange={sf("type")}         options={["Traffic Intersection","Industrial Entry","Factory Floor","IT Campus","Mall","Hospital","Transport Hub","Smart City Zone","Residential Complex","Parking Area","Warehouse"]}/>
            <Sel label="Assign Tenant"    value={siteForm.tenantId}     onChange={sf("tenantId")}     options={[{v:"",l:"-- Select Tenant --"},...(tenants||[]).map(t=>({v:t.id,l:`${t.id} -- ${t.name}`}))]}/>
            <Sel label="Jetson Node"      value={siteForm.jetsonId}     onChange={sf("jetsonId")}     options={[{v:"",l:"-- Select Jetson --"},...(jetsons||[]).map(j=>({v:j.id,l:`${j.id} -- ${j.model||j.name||""}`}))]}/>
            <Inp label="Address"          value={siteForm.address}      onChange={sf("address")}      placeholder="Full site address"/>
            <Inp label="Contact Person"   value={siteForm.contactName}  onChange={sf("contactName")}  placeholder="Site manager name"/>
            <Inp label="Contact Phone"    value={siteForm.contactPhone} onChange={sf("contactPhone")} placeholder="+91 9XXXXXXXXX"/>
            <Inp label="Latitude"         value={siteForm.lat}          onChange={sf("lat")}          placeholder="e.g. 18.5204"/>
            <Inp label="Longitude"        value={siteForm.lng}          onChange={sf("lng")}          placeholder="e.g. 73.8567"/>
          </Grid>
          <AlertBox>Site will appear in camera assignment dropdowns after creation. Cameras and zones can be added separately.</AlertBox>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn v="ghost" onClick={()=>setShowAddSite(false)}>Cancel</Btn>
            <Btn onClick={addSite} disabled={!siteForm.name||!siteForm.city}>📍 Register Site</Btn>
          </div>
        </div>
      </Modal>

      {/* Network Discovery modal */}
      <Modal open={showDiscover} title="🔍 Network Device Discovery" onClose={()=>setShowDiscover(false)} wide>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {/* Network Info Bar */}
          {netInfo&&!discovering&&(
            <div style={{background:T.faint,borderRadius:8,padding:"8px 12px",display:"flex",gap:16,fontSize:10,color:T.muted,flexWrap:"wrap",border:`1px solid ${T.border}`}}>
              <span><strong style={{color:T.text}}>Server:</strong> {(netInfo.interfaces||[]).map(i=>i.ip).join(', ')}</span>
              <span><strong style={{color:T.text}}>Gateway:</strong> {(netInfo.gateways||[]).join(', ')||'—'}</span>
              <span><strong style={{color:T.text}}>Scanned:</strong> {(netInfo.subnetsScanned||[]).map(s=>s+'.x').join(', ')}</span>
            </div>
          )}
          {!netInfo&&!discovering&&<AlertBox type="info">Scans ALL local subnets for RTSP cameras (port 554) and Jetson edge agents (port 8080). Detects cross-subnet mismatches and recommends fixes.</AlertBox>}

          {/* Scanning spinner */}
          {discovering&&(
            <div style={{textAlign:"center",padding:"24px 0"}}>
              <div style={{fontSize:36,marginBottom:8,animation:"spin 1.5s linear infinite"}}>🔍</div>
              <div style={{fontSize:12,color:T.muted,fontWeight:600}}>Scanning all subnets... this may take 15-30 seconds</div>
              <div style={{fontSize:10,color:T.muted,marginTop:4}}>Probing ports 554 (RTSP), 8080 (Edge Agent), 80 (HTTP), 22 (SSH)</div>
              <style>{`@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
            </div>
          )}

          {/* No devices */}
          {!discovering&&discovered.length===0&&netInfo&&(
            <div style={{textAlign:"center",padding:"20px 0",color:T.muted,fontSize:12}}>No devices found on {(netInfo.subnetsScanned||[]).map(s=>s+'.x').join(', ')}. Ensure camera/Jetson is powered on and connected, then retry.</div>
          )}

          {/* ── NETWORK WARNINGS ── */}
          {!discovering&&netWarnings.length>0&&(
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              <div style={{fontSize:11,fontWeight:700,color:T.red}}>⚠ Network Issues Detected</div>
              {netWarnings.map((w,i)=>(
                <div key={i} style={{background:w.severity==='critical'?T.redLo:w.severity==='high'?T.orangeLo:T.accentLo,borderRadius:8,padding:"10px 14px",border:`1px solid ${w.severity==='critical'?T.red:w.severity==='high'?T.orange:T.accent}30`}}>
                  <div style={{fontWeight:700,fontSize:12,color:w.severity==='critical'?T.red:T.orange}}>{w.icon||'⚠'} {w.title}</div>
                  <div style={{fontSize:11,color:T.text,marginTop:3}}>{w.message}</div>
                  {w.affected&&<div style={{fontSize:9,color:T.muted,fontFamily:T.mono,marginTop:3}}>Affected: {w.affected.join(', ')}</div>}
                </div>
              ))}
            </div>
          )}

          {/* ── DISCOVERED DEVICES ── */}
          {!discovering&&discovered.length>0&&(
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <div style={{fontSize:11,fontWeight:700,color:T.text}}>{discovered.length} Device(s) Found</div>
              {discovered.map((dev,i)=>{
                const onForeignSubnet = netInfo && netInfo.subnetsScanned && !netInfo.subnetsScanned.includes(dev.subnet);
                return(
                <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:T.deep,borderRadius:8,padding:"10px 14px",border:`1px solid ${onForeignSubnet?T.orange:dev.type==="camera"?T.blue:dev.type==="jetson"?T.green:T.border}30`}}>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <span style={{fontSize:22}}>{dev.type==="camera"?"📹":dev.type==="jetson"?"⚡":"🖥"}</span>
                    <div>
                      <div style={{fontWeight:700,fontSize:12}}>
                        {dev.hostname||dev.ip}
                        {onForeignSubnet&&<span style={{fontSize:9,color:T.orange,marginLeft:6,fontWeight:600}}>⚠ Different Subnet</span>}
                      </div>
                      <div style={{fontSize:10,color:T.muted,fontFamily:T.mono}}>{dev.ip}:{dev.port} — {dev.type==="camera"?"RTSP Camera":dev.type==="jetson"?"Jetson Edge Agent":"Unknown"} — subnet {dev.subnet}.x</div>
                      {dev.mac&&<div style={{fontSize:9,color:T.muted,fontFamily:T.mono}}>MAC: {dev.mac}</div>}
                      {dev.note&&<div style={{fontSize:9,color:T.teal,marginTop:1}}>{dev.note}</div>}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    {dev.type==="camera"&&<Btn sm onClick={()=>prefillCamera(dev)}>+ Add as Camera</Btn>}
                    {dev.type==="jetson"&&<Btn sm v="green" onClick={()=>addJetsonFromDiscovery(dev)}>+ Add as Jetson</Btn>}
                    {dev.type==="unknown"&&<Btn sm v="ghost" onClick={()=>prefillCamera(dev)}>Try as Camera</Btn>}
                  </div>
                </div>
              );})}
            </div>
          )}

          {/* ── NETWORK SUGGESTIONS ── */}
          {!discovering&&netSuggestions.length>0&&(
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              <div style={{fontSize:11,fontWeight:700,color:T.blue}}>💡 Recommended Actions</div>
              {netSuggestions.map((s,i)=>(
                <div key={i} style={{background:s.action==='OK'?T.greenLo:T.blueLo,borderRadius:8,padding:"10px 14px",border:`1px solid ${s.action==='OK'?T.green:T.blue}25`}}>
                  <div style={{fontWeight:700,fontSize:12,color:s.action==='OK'?T.green:T.text}}>{s.icon||'💡'} {s.title||s.reason}</div>
                  {s.title&&s.reason&&<div style={{fontSize:11,color:T.muted,marginTop:2}}>{s.reason}</div>}
                  {s.suggestedIp&&(
                    <div style={{marginTop:6,display:"flex",gap:12,fontSize:10,fontFamily:T.mono,background:T.faint,borderRadius:6,padding:"6px 10px"}}>
                      <span><strong>New IP:</strong> {s.suggestedIp}</span>
                      <span><strong>Gateway:</strong> {s.suggestedGateway}</span>
                      <span><strong>Mask:</strong> {s.suggestedMask||'255.255.255.0'}</span>
                    </div>
                  )}
                  {s.steps&&s.steps.length>0&&(
                    <div style={{marginTop:6,fontSize:10,color:T.muted,lineHeight:1.7}}>
                      {s.steps.map((step,j)=><div key={j} style={{paddingLeft:step.startsWith(' ')?16:0}}>{step.startsWith(' ')?step:`${j+1}. ${step}`}</div>)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn v="ghost" onClick={()=>setShowDiscover(false)}>Close</Btn>
            <Btn v="teal" onClick={discoverDevices} disabled={discovering}>{discovering?"Scanning...":"🔄 Re-scan Network"}</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
};
